import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Clock, Send } from "lucide-react";
import axios from "axios";
import SEOHead from "@/components/SEOHead";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactPage() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.subject || !form.message) {
      toast.error("Lütfen zorunlu alanları doldurun.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Mesajınız başarıyla gönderildi!");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch {
      toast.error("Mesaj gönderilirken bir hata oluştu.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page" className="pt-20">
      <SEOHead
        title="İletişim - Ataşehir, Kadıköy, Maltepe Spor Okulu"
        description="İRÜ Spor Kulübü iletişim bilgileri. Ataşehir, Kadıköy, Maltepe bölgesinde voleybol, basketbol, cimnastik kursu bilgi almak için bize ulaşın. Telefon: 0543 647 01 83."
        keywords="IRU spor kulübü iletişim, Kadıköy spor okulu telefon, Ataşehir spor kulübü adres, Maltepe spor eğitimi bilgi, Bostancı spor okulu iletişim"
        canonical="/iletisim"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          "name": "İRÜ Spor Kulübü",
          "telephone": "+905436470183",
          "email": "info@irusporkulubu.com",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "İstanbul Rumeli Üniversitesi Bostancı Yerleşkesi",
            "addressLocality": "İstanbul",
            "addressRegion": "Kadıköy",
            "addressCountry": "TR"
          },
          "geo": { "@type": "GeoCoordinates", "latitude": 40.9638, "longitude": 29.0958 },
          "openingHoursSpecification": {
            "@type": "OpeningHoursSpecification",
            "dayOfWeek": ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"],
            "opens": "09:00",
            "closes": "21:00"
          }
        }}
      />
      {/* Hero */}
      <section className="bg-[#1A1A1A] py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="font-accent text-sm uppercase tracking-widest text-gold">İletişim</span>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold uppercase tracking-tight text-white mt-2">
            Bize Ulaşın
          </h1>
          <p className="mt-4 text-base text-white/60 max-w-lg">
            Sorularınız için bize ulaşabilirsiniz. En kısa sürede dönüş yapacağız.
          </p>
        </div>
      </section>

      {/* Contact Info + Form */}
      <section className="py-24 lg:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 lg:gap-16">
            {/* Info */}
            <div>
              <h2 className="font-heading text-2xl font-bold uppercase tracking-tight text-[#1A1A1A]">
                İletişim Bilgileri
              </h2>
              <div className="mt-8 space-y-6">
                <div className="flex items-start gap-4 p-4 bg-surface border border-zinc-200">
                  <div className="w-10 h-10 flex items-center justify-center bg-burgundy/5 border border-burgundy/10 shrink-0">
                    <Phone className="w-4 h-4 text-burgundy" strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="text-xs font-accent uppercase tracking-wider text-gray-400">Telefon</p>
                    <a href="tel:05436470183" className="text-sm font-semibold text-[#1A1A1A] hover:text-burgundy transition-colors">
                      0543 647 01 83
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-4 bg-surface border border-zinc-200">
                  <div className="w-10 h-10 flex items-center justify-center bg-burgundy/5 border border-burgundy/10 shrink-0">
                    <Mail className="w-4 h-4 text-burgundy" strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="text-xs font-accent uppercase tracking-wider text-gray-400">E-posta</p>
                    <a href="mailto:irusporkulubu@gmail.com" className="text-sm font-semibold text-[#1A1A1A] hover:text-burgundy transition-colors">
                      irusporkulubu@gmail.com
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-4 bg-surface border border-zinc-200">
                  <div className="w-10 h-10 flex items-center justify-center bg-burgundy/5 border border-burgundy/10 shrink-0">
                    <MapPin className="w-4 h-4 text-burgundy" strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="text-xs font-accent uppercase tracking-wider text-gray-400">Adres</p>
                    <p className="text-sm font-semibold text-[#1A1A1A]">
                      Bostancı, Tünel Girişi Sk. No:16, 34744 Kadıköy/İstanbul
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-4 bg-surface border border-zinc-200">
                  <div className="w-10 h-10 flex items-center justify-center bg-burgundy/5 border border-burgundy/10 shrink-0">
                    <Clock className="w-4 h-4 text-burgundy" strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="text-xs font-accent uppercase tracking-wider text-gray-400">Çalışma Saatleri</p>
                    <p className="text-sm font-semibold text-[#1A1A1A]">
                      Pazartesi - Cumartesi: 09:00 - 20:00
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Form */}
            <div className="lg:col-span-2">
              <h2 className="font-heading text-2xl font-bold uppercase tracking-tight text-[#1A1A1A]">
                Mesaj Gönderin
              </h2>
              <form onSubmit={handleSubmit} className="mt-8 space-y-6" data-testid="contact-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="name" className="text-xs font-accent uppercase tracking-wider text-gray-500">
                      Ad Soyad *
                    </Label>
                    <Input
                      id="name"
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      data-testid="contact-name"
                      className="mt-1 rounded-none border-zinc-300 focus:border-burgundy focus:ring-burgundy"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="email" className="text-xs font-accent uppercase tracking-wider text-gray-500">
                      E-posta *
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={form.email}
                      onChange={handleChange}
                      data-testid="contact-email"
                      className="mt-1 rounded-none border-zinc-300 focus:border-burgundy focus:ring-burgundy"
                      required
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="phone" className="text-xs font-accent uppercase tracking-wider text-gray-500">
                      Telefon
                    </Label>
                    <Input
                      id="phone"
                      name="phone"
                      value={form.phone}
                      onChange={handleChange}
                      data-testid="contact-phone"
                      className="mt-1 rounded-none border-zinc-300 focus:border-burgundy focus:ring-burgundy"
                    />
                  </div>
                  <div>
                    <Label htmlFor="subject" className="text-xs font-accent uppercase tracking-wider text-gray-500">
                      Konu *
                    </Label>
                    <Input
                      id="subject"
                      name="subject"
                      value={form.subject}
                      onChange={handleChange}
                      data-testid="contact-subject"
                      className="mt-1 rounded-none border-zinc-300 focus:border-burgundy focus:ring-burgundy"
                      required
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="message" className="text-xs font-accent uppercase tracking-wider text-gray-500">
                    Mesajınız *
                  </Label>
                  <Textarea
                    id="message"
                    name="message"
                    rows={5}
                    value={form.message}
                    onChange={handleChange}
                    data-testid="contact-message"
                    className="mt-1 rounded-none border-zinc-300 focus:border-burgundy focus:ring-burgundy"
                    required
                  />
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  data-testid="contact-submit-btn"
                  className="bg-burgundy hover:bg-burgundy-700 text-white font-heading uppercase tracking-wider text-sm px-8 py-5"
                >
                  {loading ? "Gönderiliyor..." : "Mesaj Gönder"}
                  <Send className="w-4 h-4 ml-2" />
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Map */}
      <section className="bg-surface py-24" data-testid="contact-map">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="w-full aspect-[21/9] bg-gray-100 border border-zinc-200 overflow-hidden">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3013.06!2d29.0927!3d40.9611!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x14cac79e48bddd6b%3A0x7c96bbcf5e2c8d8c!2sBostanc%C4%B1%2C%20T%C3%BCnel%20Giri%C5%9Fi%20Sk.%20No%3A16%2C%2034744%20Kad%C4%B1k%C3%B6y%2F%C4%B0stanbul!5e0!3m2!1str!2str!4v1234567890"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="IRU Spor Kulubu Harita"
            />
          </div>
        </div>
      </section>
    </div>
  );
}
